import { Fragment, Slice, type MarkType, type Node as PMNode, type Schema } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';

import { isInternalWikiFileUrl } from './markdown-html';

/**
 * Вставка ссылок из буфера обмена в редактор (владелец 22.10.2026: «ссылки на файлы
 * с Диска не кликаются»).
 *
 * Кнопка «В статью» на Диске кладёт в буфер markdown `[имя](/api/wiki/disk/files/<id>/download)`.
 * Раньше редактор вставлял его как обычный текст, а при сохранении turndown экранировал
 * скобки — в читалке оставался текст вместо ссылки. Теперь:
 *  1) ровно одна ссылка на файл Диска/вложение вики в пустом абзаце → карточка файла (fkFile);
 *  2) в буфере только text/plain с markdown-ссылками `[текст](url)` → настоящие ссылки, остальной
 *     текст как есть;
 *  3) всё остальное (HTML, картинки, таблицы) — обычная вставка редактора.
 *
 * Чистые функции без DOM — покрыты paste-links.test.ts.
 */

export interface PastedFileLink {
  name: string;
  url: string;
}

/** Часть текста: просто текст или markdown-ссылка. */
export type LinkPart = { text: string } | { label: string; href: string };

/** Адрес, на который разрешаем ссылку из вставленного текста. Иное (javascript: и т.п.) остаётся текстом. */
export function safePastedHref(url: string): string | null {
  if (isInternalWikiFileUrl(url)) return url;
  if (/^https?:\/\/[^\s]+$/i.test(url)) return url;
  if (/^(?:mailto|tel):[^\s]+$/i.test(url)) return url;
  if (/^\/(?!\/)[^\s]*$/.test(url) && !url.includes('..')) return url;
  return null;
}

/** Разбирает текст на куски: обычный текст и markdown-ссылки `[текст](url)` (картинки `![..](..)` не трогаем). */
export function splitMarkdownLinks(text: string): LinkPart[] {
  const re = /\[([^[\]\n]+)\]\(([^()\s]+)\)/g;
  const parts: LinkPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const href = safePastedHref(m[2]);
    const isImage = m.index > 0 && text[m.index - 1] === '!';
    if (!href || isImage) continue;
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ label: m[1], href });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

const HTML_ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };
function decodeEntities(s: string): string {
  return s.replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, (e) => HTML_ENTITIES[e] ?? e);
}

/** Путь внутреннего файла из href (абсолютный адрес нашего же сайта тоже подходит). */
function internalPathOf(href: string, origin: string): string | null {
  const raw = decodeEntities(href.trim());
  if (isInternalWikiFileUrl(raw)) return raw;
  try {
    const u = new URL(raw, origin);
    const path = u.pathname + u.search;
    if (u.origin === origin && isInternalWikiFileUrl(path)) return path;
  } catch {
    /* не адрес — не наш случай */
  }
  return null;
}

/**
 * Ровно одна ссылка на внутренний файл вики в буфере: markdown в text/plain
 * или единственный `<a href>` в text/html (без другого текста вокруг).
 */
export function singleFileLinkFromClipboard(
  plain: string,
  html: string,
  origin: string = typeof location !== 'undefined' ? location.origin : 'http://localhost',
): PastedFileLink | null {
  const md = /^\[([^[\]\n]+)\]\(([^()\s]+)\)$/.exec(plain.trim());
  if (md) {
    const url = internalPathOf(md[2], origin);
    if (url) return { name: md[1].trim(), url };
    return null;
  }
  if (!html.trim()) return null;
  // Chrome/Edge оборачивают фрагмент в <html><body><!--StartFragment-->…; убираем обёртки и мету.
  const bare = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\/?(?:html|body|head|meta)\b[^>]*>/gi, '')
    .trim();
  const a = /^<a\s[^>]*?href\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([^<]*)<\/a>$/i.exec(bare);
  if (!a) return null;
  const url = internalPathOf(a[1] ?? a[2] ?? '', origin);
  const name = decodeEntities(a[3]).trim();
  return url && name ? { name, url } : null;
}

/**
 * Превращает markdown-ссылки внутри текстовых узлов вставляемого фрагмента в настоящие
 * ссылки (марк link). Структура фрагмента (абзацы, списки) остаётся как разобрал редактор.
 * null — ссылок нет, вставку менять не надо.
 */
export function linkifySlice(slice: Slice, schema: Schema): Slice | null {
  const linkType: MarkType | undefined = schema.marks.link;
  if (!linkType) return null;
  let changed = 0;

  const mapFragment = (frag: Fragment): Fragment => {
    const out: PMNode[] = [];
    frag.forEach((node) => {
      if (node.isText && node.text) {
        // Внутри кода ссылку не ставим (марк code исключает остальные).
        if (node.marks.some((m) => m.type.excludes(linkType))) {
          out.push(node);
          return;
        }
        const parts = splitMarkdownLinks(node.text);
        if (parts.length === 0 || (parts.length === 1 && 'text' in parts[0])) {
          out.push(node);
          return;
        }
        for (const part of parts) {
          if ('text' in part) {
            if (part.text) out.push(schema.text(part.text, node.marks));
          } else {
            out.push(schema.text(part.label, linkType.create({ href: part.href }).addToSet(node.marks)));
            changed += 1;
          }
        }
      } else if (node.content.size > 0) {
        out.push(node.copy(mapFragment(node.content)));
      } else {
        out.push(node);
      }
    });
    return Fragment.fromArray(out);
  };

  const content = mapFragment(slice.content);
  return changed > 0 ? new Slice(content, slice.openStart, slice.openEnd) : null;
}

/**
 * Транзакция для вставки из буфера или null (тогда работает обычная вставка редактора).
 * `plain`/`html` — text/plain и text/html буфера, `slice` — как редактор разобрал вставку сам.
 */
export function buildLinkPasteTransaction(
  state: EditorState,
  plain: string,
  html: string,
  slice: Slice,
  origin?: string,
): Transaction | null {
  const { selection, schema } = state;
  const $from = selection.$from;
  // В блоке кода вставка всегда текстом.
  if ($from.parent.type.spec.code) return null;

  // 1) Одна ссылка на файл в пустом абзаце → карточка файла.
  const file = singleFileLinkFromClipboard(plain, html, origin);
  const fileType = schema.nodes.fkFile;
  if (file && fileType && selection.empty && $from.parent.type.name === 'paragraph' && $from.parent.content.size === 0) {
    const card = fileType.create({ url: file.url, name: file.name, size: 0 });
    return state.tr.replaceSelectionWith(card).scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste');
  }

  // 2) Только text/plain с markdown-ссылками → настоящие ссылки.
  if (html.trim()) return null;
  const linked = linkifySlice(slice, schema);
  if (!linked) return null;
  return state.tr.replaceSelection(linked).scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste');
}
